import { VerisureItem, VerisureRepair, ResaleValuation, ItemCategory } from '../types';

/**
 * Category baseline MSRP estimation if not explicitly set
 */
const CATEGORY_DEFAULT_MSRP: Record<ItemCategory, { ngn: number; usd: number; annualDepreciation: number }> = {
  consumer_electronics: { ngn: 750000, usd: 600, annualDepreciation: 0.18 },
  home_appliance: { ngn: 450000, usd: 360, annualDepreciation: 0.12 },
  vehicle: { ngn: 12000000, usd: 9500, annualDepreciation: 0.14 },
  industrial: { ngn: 8500000, usd: 6800, annualDepreciation: 0.08 },
  telecom: { ngn: 4200000, usd: 3300, annualDepreciation: 0.10 },
  electrical: { ngn: 1800000, usd: 1400, annualDepreciation: 0.10 },
  mechanical: { ngn: 3200000, usd: 2500, annualDepreciation: 0.09 },
  medical: { ngn: 15000000, usd: 12000, annualDepreciation: 0.07 },
  aerospace: { ngn: 45000000, usd: 35000, annualDepreciation: 0.05 },
  other: { ngn: 500000, usd: 400, annualDepreciation: 0.12 },
};

/**
 * Calculates dynamic resale valuation based on equipment type, condition score, and verified repair cycles
 */
export function calculateResaleValuation(
  item: VerisureItem,
  repairs: VerisureRepair[] = []
): ResaleValuation {
  const categoryConfig = CATEGORY_DEFAULT_MSRP[item.category] || CATEGORY_DEFAULT_MSRP.other;
  const currency = item.priceCurrency || 'NGN';
  
  // Base MSRP
  const originalMSRP = item.estimatedOriginalPrice || (currency === 'USD' ? categoryConfig.usd : categoryConfig.ngn);
  
  // Age calculation
  const currentYear = new Date().getFullYear();
  const itemYear = item.year || (currentYear - 2);
  const ageYears = Math.max(0.5, currentYear - itemYear);

  // 1. Base Depreciation Calculation
  const annualDepRate = categoryConfig.annualDepreciation;
  const totalBaseDepreciation = Math.min(0.75, ageYears * annualDepRate);
  const baseDepreciatedValue = originalMSRP * (1 - totalBaseDepreciation);

  // 2. Condition Score Multiplier (0 - 100)
  const conditionScore = Math.max(10, Math.min(100, item.conditionScore || 80));
  const conditionMultiplier = 0.3 + (conditionScore / 100) * 0.75;
  const conditionAdjustedValue = baseDepreciatedValue * conditionMultiplier;

  // 3. Repair Cycles & Verification Bonus
  const repairCount = repairs.length;
  const oemPartsCount = repairs.filter(r => r.partsAuthenticity === 'oem_certified' || !r.partsAuthenticity).length;
  const oemRatio = repairCount > 0 ? oemPartsCount / repairCount : 0.8;

  // Verisure Verified Maintenance Bonus (Buyers pay +15% to +35% more for immutable, certified logs)
  let verisureBonusRate = 0.12; // Baseline trust bonus for being registered
  if (repairCount >= 1 && repairCount <= 5) {
    verisureBonusRate = 0.22; // Healthy service history
  } else if (repairCount > 5) {
    verisureBonusRate = 0.28; // Actively maintained high-use asset
  }

  // Extra bonus for OEM certified parts
  const partsAuthenticityBonus = oemRatio * 0.08;
  verisureBonusRate += partsAuthenticityBonus;

  // Condition boost if score is exceptional (>85)
  if (conditionScore >= 85) {
    verisureBonusRate += 0.05;
  }

  const verisureValueBonus = conditionAdjustedValue * verisureBonusRate;
  const currentEstimatedValue = Math.round(conditionAdjustedValue + verisureValueBonus);
  const unverifiedMarketValue = Math.round(conditionAdjustedValue * 0.85); // Unverified peer items lose ~15%

  const valueRetentionPercentage = Math.round((currentEstimatedValue / originalMSRP) * 100);
  const depreciationRate = 100 - valueRetentionPercentage;

  // Confidence Grade determination
  let confidenceGrade: 'A+' | 'A' | 'B' | 'C' | 'D' = 'B';
  if (conditionScore >= 90 && repairCount >= 1) confidenceGrade = 'A+';
  else if (conditionScore >= 80) confidenceGrade = 'A';
  else if (conditionScore >= 65) confidenceGrade = 'B';
  else if (conditionScore >= 45) confidenceGrade = 'C';
  else confidenceGrade = 'D';

  let summaryRecommendation = '';
  if (confidenceGrade === 'A+' || confidenceGrade === 'A') {
    summaryRecommendation = 'Prime Resale Tier: High buyer confidence backed by certified repair logs and OEM components.';
  } else if (confidenceGrade === 'B') {
    summaryRecommendation = 'Good Market Health: Standard wear with verified maintenance history. Fair market premium applies.';
  } else if (confidenceGrade === 'C') {
    summaryRecommendation = 'Fair Condition: Maintenance recommended prior to listing to boost valuation by up to +25%.';
  } else {
    summaryRecommendation = 'Refurbishment Advised: Critical servicing required to restore insurer audit compliance & resale value.';
  }

  return {
    originalMSRP: Math.round(originalMSRP),
    currency,
    currentEstimatedValue,
    verisureValueBonus: Math.round(verisureValueBonus),
    valueRetentionPercentage,
    depreciationRate,
    repairCyclesCount: repairCount,
    conditionScoreImpact: Math.round((conditionMultiplier - 1) * 100),
    partsAuthenticityBonus: Math.round(partsAuthenticityBonus * 100),
    confidenceGrade,
    marketComparisonUnverified: unverifiedMarketValue,
    summaryRecommendation,
  };
}
